import React, { useEffect } from 'react'

import { CreateCommentBody, TCommentThreadData } from 'types/GlobalComment'

import { useCommentActions } from 'redux/actions/Comments'
import { useCommentThreadsForReportEdition } from 'redux/hooks/Comments'
import { useCustomer } from 'redux/hooks/Customer'
import { useReportEdition } from 'redux/hooks/Reports'
import { useTasksFromReportEdition } from 'redux/hooks/Tasks'

import Box from 'components_new/atoms/Box'
import Skeleton from 'components_new/atoms/Skeleton'

import CommentThreads from 'components_new/molecules/CommentThreads'
import ToggleButton from 'components_new/molecules/ToggleButton'

import TasksList from 'components_new/organisms/tasks/TasksList'

interface ReportEditionSidebarProps {
  focusedCommentThreadId: string | null
  id: string
  newCommentThread: TCommentThreadData | null
  setFocusedCommentThreadId: React.Dispatch<React.SetStateAction<string | null>>
  setNewCommentThread: React.Dispatch<
    React.SetStateAction<TCommentThreadData | null>
  >
  setTab: React.Dispatch<React.SetStateAction<'tasks' | 'comment-threads'>>
  tab: 'tasks' | 'comment-threads'
  widgetId?: string | null
}

const ReportEditionSidebar = (props: ReportEditionSidebarProps) => {
  const {
    focusedCommentThreadId,
    id,
    newCommentThread,
    setFocusedCommentThreadId,
    setNewCommentThread,
    setTab,
    tab,
    widgetId
  } = props

  const customer = useCustomer()
  const edition = useReportEdition(id)

  const commentThreads = useCommentThreadsForReportEdition(id)
  const { tasks } = useTasksFromReportEdition(id)
  const { tryCreateComment, tryGetAllCommentsByReportEdition } =
    useCommentActions()

  useEffect(() => {
    if (edition?.locked_at) {
      tryGetAllCommentsByReportEdition(id)
    }
  }, [id, edition?.locked_at])

  const handleCreateComment = (body: CreateCommentBody) => {
    tryCreateComment(body, () => {
      tryGetAllCommentsByReportEdition(id)
    })
  }

  // loading
  if (!edition)
    return (
      <>
        <Skeleton height={36} variant="rounded" />
        <Skeleton height={32} variant="rounded" />

        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={96} variant="rounded" />
        ))}
      </>
    )

  // success
  return (
    <>
      <ToggleButton
        items={[
          ...(customer?.allow_access.allow_key_metrics
            ? [
                {
                  iconName: 'Tasks' as const,
                  title: `${tasks.length}`,
                  value: 'tasks'
                }
              ]
            : []),
          {
            iconName: 'CommentThread',
            title: `${commentThreads.length}`,
            value: 'comment-threads'
          }
        ]}
        onChange={(_, value) => setTab(value)}
        size="small"
        value={tab}
      />

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 1,
          minHeight: 0,
          overflowY: 'auto'
        }}
      >
        {tab === 'tasks' ? (
          <TasksList
            editMode={true}
            hideLinkedKeyMetric={true}
            sx={{ flex: 1 }}
            tasks={tasks}
          />
        ) : (
          <CommentThreads
            commentThreads={commentThreads}
            focusedCommentThreadId={focusedCommentThreadId}
            widgetId={widgetId}
            newCommentThread={newCommentThread}
            onCreateComment={handleCreateComment}
            setFocusedCommentThreadId={setFocusedCommentThreadId}
            setNewCommentThread={setNewCommentThread}
          />
        )}
      </Box>
    </>
  )
}

export default ReportEditionSidebar
